import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useStore } from "@/context/StoreContext";

const WINDOW = 3600000; // celebrate the first hour after opening

const fmt = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  const p = (n) => String(n).padStart(2, "0");
  return `${p(Math.floor(s / 3600))}:${p(Math.floor((s % 3600) / 60))}:${p(s % 60)}`;
};

// A tasteful ribbon shown only during the first hour after the store opens,
// so early visitors feel the moment they walked in on opening day.
const DoorsOpen = () => {
  const { settings } = useStore();
  const launchAt = settings?.launch_at ? new Date(settings.launch_at).getTime() : null;
  const [now, setNow] = useState(Date.now());
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem("nba_doors_seen") === "1"; } catch { return false; }
  });

  useEffect(() => {
    if (!launchAt) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [launchAt]);

  if (!launchAt || dismissed) return null;
  const since = now - launchAt;
  if (since < 0 || since > WINDOW) return null;

  const close = () => {
    setDismissed(true);
    try { sessionStorage.setItem("nba_doors_seen", "1"); } catch {}
  };

  return (
    <AnimatePresence>
      <motion.div
        data-testid="doors-open-ribbon"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="fixed bottom-24 left-6 z-[45] flex items-center gap-4 rounded-full border border-gold/40 bg-cocoa/90 backdrop-blur-md pl-6 pr-3 py-3 shadow-xl">
        <div className="leading-tight">
          <p className="font-serif text-cream text-lg">The doors are open — welcome in.</p>
          <p className="text-[0.58rem] tracking-[0.22em] uppercase text-gold/80">
            Opening hour · {fmt(WINDOW - since)} of celebration left
          </p>
        </div>
        <button
          onClick={close}
          data-testid="doors-open-dismiss"
          aria-label="Dismiss"
          className="text-cream/60 hover:text-gold transition-colors p-1">
          <X size={16} />
        </button>
      </motion.div>
    </AnimatePresence>
  );
};

export default DoorsOpen;
